# Shellfish-Free Dietary Guidance

**CORE PRINCIPLE:** Eliminate all crustaceans and molluscs — shrimp, prawns, crab, lobster, crayfish, clams, mussels, oysters, scallops, squid, octopus — while preserving the briny depth and umami they provide through fish-free where needed, and shellfish-free-always alternatives. Shellfish allergy is one of the most common and can be severe; treat as an allergy-level restriction.

## CRITICAL SAFETY RULES

### Severity Awareness
**PRINCIPLE:** Shellfish allergy (especially crustacean) can cause anaphylaxis; hidden shellfish derivatives are a real danger

- Never suggest "a little fish sauce is fine" when adapting a shellfish dish — fish sauce is fine (it's fish), but oyster sauce is NOT
- Specify checking labels on Asian sauces: oyster sauce, hoisin (some brands), some fish sauces contain shellfish
- Warn about cross-contamination in restaurants (shared woks, fryers) in recipe notes when relevant
- Iodine/contrast-dye myths: not directly related to shellfish allergy; do not repeat them

**CRUSTACEANS vs MOLLUSCS:**
- Some people allergic to crustaceans tolerate molluscs and vice versa — but default to excluding BOTH unless the user explicitly says otherwise
- When the user says "shellfish allergy", exclude both categories entirely

## HIDDEN SHELLFISH SOURCES

### Label Vigilance
**PRINCIPLE:** Shellfish derivatives hide in condiments and processed foods

- **Oyster sauce** — the classic hidden danger in stir-fries; replace with vegetarian oyster sauce (mushroom-based) or the substitute below
- **Fish sauce** — generally shellfish-free (anchovy-based) but some brands process shellfish; specify checking the label
- **Hoisin sauce** — most brands are shellfish-free, verify
- **Surimi/imitation crab** — made from fish BUT processed on shellfish lines and often contains crab extract; avoid
- **Seafood stock, paella/bouillabaisse bases, seafood flavour packets** (ramen, chips, instant noodles)
- **Worcestershire sauce** — anchovy-based, shellfish-free; safe unless combined allergy
- **Caviar substitute, salad dressings with seafood flavouring, some Caesar dressings** (anchovy, not shellfish — safe)
- **Glucosamine supplements** — often shellfish-derived (relevant only if mentioning nutrition)

## UMAMI AND BRINE REPLACEMENTS

### Rebuilding the Sea Flavour
**PRINCIPLE:** Shellfish contribute salt, umami (glutamates), sweetness, and oceanic aroma — rebuild in layers

**UMAMI BUILDERS (shellfish-free):**
- Fish sauce (label-checked), or vegan fish sauce (seaweed + mushroom) for full seafood-free
- Dried shiitake mushrooms (soaked; use the soaking liquid too)
- Kombu/kelp or a strip of nori simmered in the broth, removed before serving
- Miso paste, especially white/shiro in lighter dishes
- Toasted sesame oil for richness, soy sauce/tamari for salt-umami
- Tomato paste (surprisingly effective in chowder-like bases)
- Cured pork (bacon, pancetta, chorizo) where the dish isn't vegetarian — the traditional partner of clams for a reason

**BRINE AND OCEANIC AROMA:**
- Capers + their brine, chopped olives
- Lemon zest and juice (brightens anything pretending to be seaside)
- A few drops of dulse or other seaweed flakes
- Salt levels: shellfish are self-seasoning; when removed, dishes often need MORE salt and acid than expected — season aggressively and finish with lemon

## TEXTURE REPLACEMENTS

### Matching the Bite
**PRINCIPLE:** Shellfish textures range from tender-snap (shrimp) to chewy (squid) to delicate (scallops) — pick the right analogue per dish

**TENDER-SNAP (shrimp/prawns):**
- Firm white fish (cod, haddock, pollock) in generous chunks — the default swap in curries, stir-fries, tacos
- King oyster mushrooms, sliced into rounds and scored — shockingly shrimp-like when seared
- Hearts of palm (great in "crab" cakes — the classic vegan crab cake move)
- Lobster mushroom (an actual mushroom with seafood aroma) when available

**CHEWY (squid/octopus/clams):**
- Oyster mushrooms or king oysters, roasted until edges crisp
- Sliced and seared king trumpet stems in garlic butter analogues
- Shiitake caps, scored and seared hard

**DELICATE (scallops):**
- Thick-cross-section king oyster rounds, seared in brown butter (or vegan butter) — the closest visual and textural match
- Thick white fish medallions for baked gratin-style dishes

**LUMP/CRABMEAT (crab cakes, bisques):**
- Hearts of palm shredded + artichoke + the classic seasoning blend (Old Bay/paprika/mustard)
- Jackfruit (pulled, seasoned, seared) for bulk
- Chickpea-based "tuna" style mash for salads

## COMMON DISH ADAPTATIONS

### Typical Shellfish-Heavy Dishes
**PRINCIPLE:** Rework the flavour base so the dish stands on its own rather than tasting like something is missing

- **Pad thai** — replace shrimp with firm tofu or chicken; ensure the sauce uses label-checked fish sauce, never oyster sauce
- **Paella** — build with chicken, chorizo, and/or firm fish and mussels-omitted; use saffron, smoked paprika, and a rich stock (label-checked for seafood-free if needed)
- **Linguine alle vongole** — pasta with garlic, chilli, white wine, parsley, and toasted panko "clams" or white beans; the sauce carries the dish
- **Bisque (lobster/shrimp/crab)** — roasted tomato or roasted red pepper base with cream, cognac or sherry, and paprika/cayenne; blend smooth
- **Cioppino/seafood stew** — firm white fish, and if fish is acceptable, mussels-free version with extra potatoes and fennel
- **Sushi/poke** — salmon/tuna (fish-only if tolerated) or tofu/avocado/cucumber rolls; avoid surimi
- **Shrimp toast/Scampi** — minced chicken or king oyster mushroom version in the garlic-butter-wine sauce
- **Oyster sauce stir-fries** — vegetarian oyster sauce (mushroom-based, widely available) or soy + a little sugar + mushroom-soaking liquid

## AVOID ENTIRELY

- Shrimp, prawns, langoustine, crayfish, crab, lobster (crustaceans)
- Clams, mussels, oysters, scallops, cockles (bivalves)
- Squid, octopus, cuttlefish (cephalopods)
- Snails, abalone, whelk (gastropods)
- Oyster sauce and any sauce/stock labelled seafood or shellfish
- Surimi/imitation seafood
- Seafood-flavoured instant noodles, chips, crackers
- Cross-contamination: restaurant woks and fryers, seafood counters slicing for both

## NOTE FOR RECIPE OUTPUT

When generating a Shellfish-Free recipe, add a brief note in the recipe notes flagging which sauces/condiments were chosen as verified shellfish-free (e.g. "mushroom-based vegetarian oyster sauce" or "check your fish sauce label") and remind readers with severe allergies about cross-contamination in restaurants and shared cooking surfaces.

This guidance ensures shellfish-free recipes are safe by default and genuinely satisfying — rebuilt with real umami depth and honest texture swaps, not just shellfish dishes with the main event deleted.
